import * as React from "react";
import { DayPicker, useDayPicker } from "react-day-picker";
import type { MonthCaptionProps } from "react-day-picker";
import { setMonth, setYear } from "date-fns";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";

export type CalendarProps = React.ComponentProps<typeof DayPicker>;

const MONTHS = [
	"January", "February", "March", "April", "May", "June",
	"July", "August", "September", "October", "November", "December",
];

/* ── Custom caption: ← [Month ▾] [Year ▾] → ── */
function CustomMonthCaption({ calendarMonth }: MonthCaptionProps) {
	const { goToMonth, nextMonth, previousMonth, dayPickerProps } = useDayPicker();

	const startYear = (dayPickerProps.startMonth ?? new Date()).getFullYear();
	const endYear   = (dayPickerProps.endMonth   ?? new Date(new Date().getFullYear() + 10, 11)).getFullYear();
	const years     = Array.from({ length: endYear - startYear + 1 }, (_, i) => startYear + i);

	const currentMonth = calendarMonth.date.getMonth();
	const currentYear  = calendarMonth.date.getFullYear();

	return (
		<div className="flex items-center justify-between gap-2 px-1 pb-3 border-b border-gray-100">
			{/* Prev arrow */}
			<button
				type="button"
				aria-label="Previous month"
				disabled={!previousMonth}
				onClick={() => previousMonth && goToMonth(previousMonth)}
				className={cn(
					"inline-flex h-8 w-8 items-center justify-center rounded-md",
					"border border-gray-200 bg-white text-gray-500",
					"hover:bg-gray-50 hover:text-gray-800 transition-colors",
					"disabled:opacity-30 disabled:cursor-not-allowed"
				)}
			>
				<ChevronLeft className="h-4 w-4" />
			</button>

			{/* Month + Year selects */}
			<div className="flex items-center gap-1.5">
				<select
					value={currentMonth}
					onChange={(e) => goToMonth(setMonth(calendarMonth.date, Number(e.target.value)))}
					className="h-8 cursor-pointer rounded-md border border-gray-200 bg-white px-2 text-sm font-medium text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500 hover:border-gray-300 transition-colors"
				>
					{MONTHS.map((m, i) => (
						<option key={m} value={i}>{m}</option>
					))}
				</select>

				<select
					value={currentYear}
					onChange={(e) => goToMonth(setYear(calendarMonth.date, Number(e.target.value)))}
					className="h-8 cursor-pointer rounded-md border border-gray-200 bg-white px-2 text-sm font-medium text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500 hover:border-gray-300 transition-colors"
				>
					{years.map((y) => (
						<option key={y} value={y}>{y}</option>
					))}
				</select>
			</div>

			{/* Next arrow */}
			<button
				type="button"
				aria-label="Next month"
				disabled={!nextMonth}
				onClick={() => nextMonth && goToMonth(nextMonth)}
				className={cn(
					"inline-flex h-8 w-8 items-center justify-center rounded-md",
					"border border-gray-200 bg-white text-gray-500",
					"hover:bg-gray-50 hover:text-gray-800 transition-colors",
					"disabled:opacity-30 disabled:cursor-not-allowed"
				)}
			>
				<ChevronRight className="h-4 w-4" />
			</button>
		</div>
	);
}

function Calendar({
	className,
	classNames,
	showOutsideDays = true,
	...props
}: CalendarProps) {
	return (
		<DayPicker
			showOutsideDays={showOutsideDays}
			className={cn("p-3 w-[280px]", className)}
			classNames={{
				months:        "flex flex-col",
				month:         "flex flex-col gap-4",
				month_caption: "relative",
				caption_label: "hidden",
				nav:           "hidden",
				month_grid:    "w-full border-collapse",
				weekdays:      "flex mb-1",
				weekday:       "flex-1 text-center text-xs font-medium text-gray-400 uppercase tracking-wide",
				weeks:         "flex flex-col gap-0.5",
				week:          "flex w-full",
				day:           "flex-1 p-0 text-center",
				day_button:    cn(
					"mx-auto flex h-9 w-9 items-center justify-center rounded-md text-sm",
					"text-gray-700 hover:bg-blue-50 hover:text-blue-700",
					"focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1",
					"transition-colors cursor-pointer"
				),
				selected:  "bg-blue-600 text-white hover:bg-blue-700 hover:text-white font-semibold rounded-md",
				today:     "border border-blue-300 font-semibold text-blue-600 rounded-md",
				outside:   "text-gray-300 opacity-50",
				disabled:  "text-gray-300 cursor-not-allowed opacity-50",
				hidden:    "invisible",
				...classNames,
			}}
			components={{ MonthCaption: CustomMonthCaption }}
			{...props}
		/>
	);
}

Calendar.displayName = "Calendar";

export { Calendar };
